import { useState } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secret: string;
}

/**
 * Shown ONCE after webhook creation. The HMAC secret is irretrievable
 * after this dialog closes — admin must delete + recreate to rotate.
 * Matches the API-key reveal-once UX pattern.
 */
export function WebhookSecretDialog({ open, onOpenChange, secret }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="text-brand-600 h-5 w-5" />
            Webhook secret — แสดงครั้งเดียว
          </DialogTitle>
          <DialogDescription>
            เก็บค่านี้ในที่ปลอดภัย — receiver ใช้ verify HMAC-SHA256 signature ใน header
            <code className="ml-1 rounded bg-slate-100 px-1 font-mono text-[11px] dark:bg-slate-800">
              X-Webhook-Signature
            </code>
            . ปิด dialog แล้วจะดูซ้ำไม่ได้
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <code className="block break-all rounded-md border border-amber-200 bg-amber-50 px-3 py-3 font-mono text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
            {secret}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void handleCopy();
            }}
          >
            {copied ? (
              <>
                <Check className="mr-1.5 h-3.5 w-3.5" /> คัดลอกแล้ว
              </>
            ) : (
              <>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> คัดลอก secret
              </>
            )}
          </Button>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>เก็บแล้ว เข้าใจแล้ว</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
